import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/hooks/useAuth";

/**
 * AdminMetrics — protected investor dashboard (Sprint 3, Monish Prompt H).
 * Route: /admin/metrics
 *
 * Server already enforces admin via `settings_svc.is_admin(email)`. The
 * frontend simply redirects on 403.
 */
const INDIGO = "#5D00FF";
const FUCHSIA = "#FF2E9A";
const INK = "#1C1C1C";
const MUTE = "#676773";

function Metric({ label, value, hint }) {
  return (
    <div
      data-testid={`metric-${label.toLowerCase().replace(/\W+/g, "-")}`}
      style={{
        padding: "20px 22px",
        background: "rgba(255,255,255,0.78)",
        border: "1px solid rgba(28,28,28,0.08)",
        borderRadius: 18,
        boxShadow: "0 6px 20px rgba(30,20,70,0.04)",
        minWidth: 180,
      }}
    >
      <div style={{ color: MUTE, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase" }}>{label}</div>
      <div
        style={{
          color: INK,
          fontFamily: "Cormorant Garamond, Georgia, serif",
          fontSize: 36,
          fontWeight: 300,
          marginTop: 6,
          lineHeight: 1,
        }}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {hint && (
        <div style={{ color: MUTE, fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 36 }}>
      <h2
        style={{
          color: INDIGO,
          fontSize: 11,
          letterSpacing: "0.34em",
          textTransform: "uppercase",
          marginBottom: 14,
        }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>{children}</div>
    </section>
  );
}

export default function AdminMetrics() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get("/admin/metrics")
      .then((r) => setData(r.data))
      .catch((e) => {
        const s = e?.response?.status;
        if (s === 403) {
          setErr("Admin only");
          setTimeout(() => nav("/app"), 1200);
        } else if (s === 401) {
          setErr("Please sign in with an admin account.");
          setTimeout(() => nav("/"), 1500);
        } else {
          setErr(e?.message || "Failed to load");
        }
      });
  }, [nav]);

  if (err) {
    return (
      <div style={{ minHeight: "100dvh", display: "grid", placeItems: "center", background: "#FAFAFC" }}>
        <div style={{ color: MUTE }}>{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ minHeight: "100dvh", display: "grid", placeItems: "center", background: "#FAFAFC" }}>
        <div style={{ color: MUTE, letterSpacing: "0.3em", fontSize: 11 }}>LOADING METRICS…</div>
      </div>
    );
  }

  const p = data;

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "#FAFAFC",
        backgroundImage: "radial-gradient(#DCE2EA 1px, transparent 1px)",
        backgroundSize: "24px 24px",
        padding: "32px 32px 80px",
      }}
      data-testid="admin-metrics-page"
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ color: INDIGO, fontSize: 11, letterSpacing: "0.4em" }}>KALKI</div>
            <h1
              style={{
                color: INK,
                fontFamily: "Cormorant Garamond, Georgia, serif",
                fontWeight: 300,
                fontSize: 42,
                marginTop: 6,
                letterSpacing: "-0.01em",
              }}
            >
              Investor Metrics
            </h1>
          </div>
          <div style={{ color: MUTE, fontSize: 12 }}>
            Generated {new Date(p.generated_at).toLocaleString()}
          </div>
        </div>

        <Section title="Signups">
          <Metric label="Total" value={p.signups.total} />
          <Metric label="Last 30d" value={p.signups.last_30d} />
          <Metric label="Onboarded" value={p.signups.onboarding_complete} hint="Users with full birth intake" />
        </Section>

        <Section title="Activity">
          <Metric label="DAU" value={p.activity.dau} />
          <Metric label="WAU" value={p.activity.wau} />
          <Metric label="MAU" value={p.activity.mau} />
        </Section>

        <Section title="Conversations">
          <Metric label="Conversations" value={p.conversations.total} />
          <Metric label="Messages" value={p.conversations.messages_total} />
          <Metric label="Messages 7d" value={p.conversations.messages_7d} />
          <Metric
            label="Failed replies"
            value={p.conversations.failed_replies}
            hint="Chat retries that exhausted all 3 attempts"
          />
        </Section>

        <Section title="Predictions">
          <Metric label="Total" value={p.predictions.total} />
          <Metric label="Active" value={p.predictions.by_state.ACTIVE} />
          <Metric label="Watching" value={p.predictions.by_state.WATCHING} />
          <Metric label="Verified (self)" value={p.predictions.by_state.VERIFIED} />
          <Metric label="Did not happen" value={p.predictions.by_state.DID_NOT_HAPPEN} />
        </Section>

        <Section title="Life Reports">
          <Metric label="Total" value={p.reports.total} />
          <Metric label="Last 30d" value={p.reports.last_30d} />
        </Section>

        <Section title="Credits">
          <Metric label="Granted" value={p.credits.granted} hint="Welcome + referral + admin + special" />
          <Metric label="Spent" value={p.credits.spent} hint="Reports, Chat, Blessings" />
          <Metric label="Outstanding" value={p.credits.net_outstanding} hint="granted − spent" />
        </Section>

        <Section title="Referrals">
          <Metric label="Total" value={p.referrals.total} />
          <Metric label="Credited" value={p.referrals.credited} />
        </Section>

        {/* Sprint B — v0.3 Prompt 8: reply feedback health */}
        {p.feedback && (
          <Section title="Reply feedback (Sprint B)">
            <Metric label="👍 Up" value={p.feedback.up} />
            <Metric label="👎 Down" value={p.feedback.down} />
            <Metric
              label="Helpful %"
              value={p.feedback.helpful_pct != null ? `${p.feedback.helpful_pct}%` : "—"}
              hint="up / (up + down) across all replies"
            />
            {Array.isArray(p.feedback.top_complaints) && p.feedback.top_complaints.length > 0 && (
              <div style={{ gridColumn: "1 / -1", marginTop: 8, color: MUTE, fontSize: 12 }}>
                <strong style={{ color: INK }}>Top complaints:</strong>{" "}
                {p.feedback.top_complaints.map((c) => `${c.reason} (${c.count})`).join(" · ")}
              </div>
            )}
          </Section>
        )}

        {/* Sprint B — v0.3 Prompt 2: karma funnel */}
        {p.karma_steps && (
          <Section title="Karma funnel (Sprint B)">
            <Metric label="Steps added" value={p.karma_steps.added} />
            <Metric label="Marked done" value={p.karma_steps.done} />
            <Metric
              label="Done %"
              value={p.karma_steps.done_pct != null ? `${p.karma_steps.done_pct}%` : "—"}
              hint="done / added — simplest 'did it actually help?' signal"
            />
            <Metric label="Skipped" value={p.karma_steps.skipped} />
          </Section>
        )}

        {/* Sprint B — v0.3 Prompt 6: safety classifier breakdown */}
        {p.safety?.by_category && Object.keys(p.safety.by_category).length > 0 && (
          <Section title="Safety classifier (Sprint B)">
            {Object.entries(p.safety.by_category).map(([cat, n]) => (
              <Metric key={cat} label={cat.replace(/_/g, " ")} value={n} />
            ))}
            <div style={{ gridColumn: "1 / -1", marginTop: 8, color: MUTE, fontSize: 12 }}>
              Counts only — the user's text is never stored for safety events.
            </div>
          </Section>
        )}

        <div
          data-testid="metrics-disclaimer"
          style={{
            marginTop: 48,
            padding: "16px 20px",
            background: "rgba(255,46,154,0.06)",
            border: "1px solid rgba(255,46,154,0.18)",
            borderRadius: 14,
            color: MUTE,
            fontSize: 12,
            lineHeight: 1.7,
            maxWidth: 760,
          }}
        >
          <strong style={{ color: FUCHSIA }}>Honesty note:</strong>{" "}
          Prediction accuracy numbers are self-reported outcome labels only — KALKI never
          claims scientifically validated predictive accuracy. {p.privacy_note}
        </div>
      </div>
    </div>
  );
}
